/* eslint-disable no-unused-vars */
import React, {useEffect, useCallback} from 'react'
import {useNavigate, useLocation, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {KTCard, KTCardBody, KTCardHeader, Select, Loading} from '../../../components'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {basicLookup} from '../../../redux/slices/lookup.slice'
import {
  subcontractorDocumentGet,
  subcontractorDocumentUpdate,
  setRedirect,
} from '../../../redux/slices/subcontractordocument.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedBreadcrumbs,
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  SubcontractorDocumentState: state.lookup.SubcontractorDocumentState,
})

const selectSubcontractorDocumentData = (state) => ({
  detail: state.subcontractorDocument.detail,
  isRedirect: state.subcontractorDocument.isRedirect,
  loading: state.subcontractorDocument.loading,
})

function Edit() {
  const intl = useIntl()
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)

  const {SubcontractorDocumentState} = useSelector(selectLookupData)
  const {detail, isRedirect, loading} = useSelector(selectSubcontractorDocumentData)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(setRedirect(false))
    if (id) dispatch(subcontractorDocumentGet({Id: Number(id)}))
    dispatch(basicLookup('SubcontractorDocumentState'))
  }, [dispatch, id])

  const validSchema = useMemoizedValidationSchema(
    () =>
      Yup.object().shape({
        SubcontractorDocumentStateId: Yup.number()
          .required(intl.formatMessage({id: 'REQUIRED.GENERAL'}))
          .moreThan(0, intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      }),
    [intl]
  )

  const handleSubmit = useCallback(
    async (values, {setSubmitting}) => {
      setSubmitting(true)
      dispatch(subcontractorDocumentUpdate(values))
    },
    [dispatch]
  )

  const formik = useFormik({
    initialValues: {
      Id: Number(id),
      Description: (detail && detail.description) || '',
      SubcontractorDocumentStateId: (detail && detail.subcontractorDocumentStateId) || '',
    },
    enableReinitialize: true,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  const handleRedirect = useCallback(() => {
    if (isRedirect) {
      navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)
    }
  }, [isRedirect, navigate, pageUrl, id])

  useEffect(() => {
    handleRedirect()
  }, [handleRedirect])

  const breadcrumbs = useMemoizedBreadcrumbs(
    () => [
      {
        title: intl.formatMessage({id: 'BC.SUBCONTRACTORDOCUMENT'}),
        path: '/projects/subcontractor-document',
        isSeparator: false,
        isActive: false,
      },
    ],
    [intl]
  )

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.SUBCONTRACTORDOCUMENT.EDIT'})}
      </PageTitle>
      <KTCard>
        <KTCardHeader title={intl.formatMessage({id: 'BC.SUBCONTRACTORDOCUMENT.EDIT'})} />
        <KTCardBody>
          {detail && (
            <form className='form' onSubmit={formik.handleSubmit} noValidate>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.CREATE.COMPANYID'})}
                </label>
                <div className='col-lg-6 fv-row d-flex align-items-center'>
                  <span className='fw-bold text-gray-800'>{detail.companyTitle}</span>
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.CREATE.PROJECTID'})}
                </label>
                <div className='col-lg-6 fv-row d-flex align-items-center'>
                  <span className='fw-bold text-gray-800'>{detail.projectName}</span>
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.SEARCH.PERIOD'})}
                </label>
                <div className='col-lg-6 fv-row d-flex align-items-center'>
                  <span className='fw-bold text-gray-800'>
                    {detail.year} /{' '}
                    {intl.formatDate(new Date(2000, (detail.month || 1) - 1, 1), {month: 'long'})}
                  </span>
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.SEARCH.STATE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={SubcontractorDocumentState}
                    required
                    {...formik.getFieldProps('SubcontractorDocumentStateId')}
                    onChange={(item) =>
                      formik.setFieldValue('SubcontractorDocumentStateId', Number(item && item.value))
                    }
                  />
                  {formik.touched.SubcontractorDocumentStateId &&
                    formik.errors.SubcontractorDocumentStateId && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.SubcontractorDocumentStateId}</span>
                        </div>
                      </div>
                    )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.CREATE.DESCRIPTION'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <textarea
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    rows={5}
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('Description')}
                  />
                </div>
              </div>

              <div className='row pt-15'>
                <div className='col-lg-9 offset-lg-3 fv-row fv-plugins-icon-container d-flex gap-3'>
                  <button
                    type='button'
                    className='btn btn-light'
                    onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)}
                  >
                    {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    data-kt-users-modal-action='submit'
                    disabled={formik.isSubmitting || !formik.isValid}
                  >
                    <span className='indicator-label'>
                      {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                    </span>
                    {formik.isSubmitting && (
                      <span className='indicator-progress'>
                        {intl.formatMessage({id: 'PLEASE'})}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {Edit}
